import React, { useCallback, useEffect, useMemo, useState } from "react";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { DateRangePicker } from "react-dates";
import {
  FaChartBar,
  FaChevronLeft,
  FaChevronRight,
  FaPhoneAlt,
  FaSearch,
  FaSms,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import CustomModal from "../components/modal";
import { setActivePage, setShowPreloader } from "../store/activePage";
import {
  getEventAutomationReport,
  getEventAutomationReportDetail,
} from "../hooks/getEventAutomationReport";

const parseIndiaDate = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const normalized = raw.replace(/ /g, "T");
  if (/Z|[+-]\d{2}:?\d{2}$/.test(normalized)) {
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );
  if (match) {
    const [, year, month, day, hour, minute, second = "00"] = match;
    const utcMs =
      Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
      ) - 5.5 * 60 * 60 * 1000;
    return new Date(utcMs);
  }
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

function formatDateTime(value) {
  if (!value) return "Not available";
  const date = parseIndiaDate(value);
  if (!date) return String(value);
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function SummaryCard({ label, value, hint, tone = "slate" }) {
  const toneClasses = {
    slate: "border-slate-200/80 bg-white text-slate-900",
    emerald: "border-emerald-200 bg-emerald-50/80 text-emerald-900",
    rose: "border-rose-200 bg-rose-50/80 text-rose-900",
    sky: "border-sky-200 bg-sky-50/80 text-sky-900",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 shadow-sm ${toneClasses[tone]}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
        {label}
      </p>
      <div className="mt-2 flex items-end justify-between gap-3">
        <p className="text-2xl font-black leading-none">{value}</p>
        <p className="max-w-[9rem] text-right text-[11px] leading-4 text-slate-500">{hint}</p>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const value = (status || "unknown").toLowerCase();
  const styles = {
    completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
    processing: "border-amber-200 bg-amber-50 text-amber-700",
    scheduled: "border-sky-200 bg-sky-50 text-sky-700",
    failed: "border-rose-200 bg-rose-50 text-rose-700",
    not_available: "border-slate-200 bg-slate-100 text-slate-600",
    not_enabled: "border-slate-200 bg-slate-100 text-slate-600",
    unknown: "border-slate-200 bg-slate-100 text-slate-600",
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-wide ${styles[value] || styles.unknown}`}>
      {value.replaceAll("_", " ")}
    </span>
  );
}

function ChannelCard({ icon, title, report, tone }) {
  const Icon = icon;
  const bgClass =
    tone === "sms"
      ? "from-sky-50 via-white to-cyan-50 border-sky-200"
      : "from-emerald-50 via-white to-teal-50 border-emerald-200";

  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${bgClass} p-3`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[13px] font-black text-slate-900">
            <Icon className={tone === "sms" ? "text-sky-600" : "text-emerald-600"} />
            {title}
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            Scheduled: {formatDateTime(report?.scheduled_at)}
          </p>
        </div>
        <StatusPill status={report?.status} />
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        <div className="rounded-xl bg-white/85 px-2.5 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Success</p>
          <p className="mt-1 text-lg font-black leading-none text-emerald-700">{report?.success_count || 0}</p>
        </div>
        <div className="rounded-xl bg-white/85 px-2.5 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Failed</p>
          <p className="mt-1 text-lg font-black leading-none text-rose-700">{report?.failed_count || 0}</p>
        </div>
        <div className="rounded-xl bg-white/85 px-2.5 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Pending</p>
          <p className="mt-1 text-lg font-black leading-none text-amber-700">{report?.pending_count || 0}</p>
        </div>
        <div className="rounded-xl bg-white/85 px-2.5 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Missed</p>
          <p className="mt-1 text-lg font-black leading-none text-slate-700">{report?.unmatched_count || 0}</p>
        </div>
      </div>
    </div>
  );
}

function DetailList({ title, items }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-[13px] font-black text-slate-900">{title}</h3>
        <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-slate-600">
          {items.length} recipients
        </span>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-5 text-center text-xs text-slate-500">
          No provider detail available yet.
        </div>
      ) : (
        <div className="max-h-[42vh] overflow-y-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Mobile</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Provider</th>
                <th className="px-3 py-2 text-left">Error</th>
                <th className="px-3 py-2 text-left">Updated</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={`${item.mobile}-${item.updated_at}`} className="border-t border-slate-100">
                  <td className="px-3 py-2.5 font-bold text-slate-900">{item.mobile}</td>
                  <td className="px-3 py-2.5"><StatusPill status={item.status} /></td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{item.provider_status || "-"}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{item.error_message || item.error_code || "-"}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{formatDateTime(item.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function isChannelEnabled(report) {
  return Boolean(report?.enabled);
}

const OperatorAutomationReport = () => {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({
    events_with_automation: 0,
    sms_success: 0,
    sms_failed: 0,
    voice_success: 0,
    voice_failed: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Automation Report",
        activeMenu: "automationReport",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchValue(searchValue.trim());
      setPage(1);
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate]);

  const loadReport = useCallback(async () => {
    dispatch(setShowPreloader(true));
    const data = await getEventAutomationReport({
      startDate: startDate?.format("YYYY-MM-DD") || "",
      endDate: endDate?.format("YYYY-MM-DD") || "",
      searchValue: debouncedSearchValue,
      page,
      perPage: 10,
    });
    dispatch(setShowPreloader(false));
    setItems(Array.isArray(data?.items) ? data.items : []);
    setSummary(
      data?.summary || {
        events_with_automation: 0,
        sms_success: 0,
        sms_failed: 0,
        voice_success: 0,
        voice_failed: 0,
      }
    );
    setPagination(
      data?.pagination || {
        page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
      }
    );
  }, [debouncedSearchValue, dispatch, endDate, page, startDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const cards = useMemo(
    () => [
      {
        label: "Events",
        value: summary.events_with_automation || 0,
        hint: "Events with reminder SMS or voice enabled",
        tone: "slate",
      },
      {
        label: "SMS Success",
        value: summary.sms_success || 0,
        hint: "Delivered SMS reminder count",
        tone: "sky",
      },
      {
        label: "Voice Success",
        value: summary.voice_success || 0,
        hint: "Answered voice reminder count",
        tone: "emerald",
      },
      {
        label: "Total Failures",
        value: (summary.sms_failed || 0) + (summary.voice_failed || 0),
        hint: "Combined SMS and voice failures",
        tone: "rose",
      },
    ],
    [summary]
  );

  return (
    <div className="p-4 lg:p-5">
      <div className="rounded-[1.75rem] border border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-teal-900 px-5 py-4 text-white shadow-lg shadow-slate-200/60">
        <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-teal-200">
          Enterprise Dashboard
        </p>
        <div className="mt-2 flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <h1 className="text-2xl font-black tracking-tight">
          Event Automation Report
          </h1>
          <p className="max-w-2xl text-xs leading-5 text-slate-200">
            Track SMS and voice reminder outcomes per event in a compact live report.
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <SummaryCard key={card.label} {...card} />
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto]">
          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search by customer, mobile, event type, or city"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-4 text-sm font-semibold text-slate-800 outline-none transition focus:border-teal-400 focus:bg-white"
            />
          </div>

          <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <DateRangePicker
              startDate={startDate}
              startDateId="automation-report-start-date"
              endDate={endDate}
              endDateId="automation-report-end-date"
              onDatesChange={({ startDate: nextStartDate, endDate: nextEndDate }) => {
                setStartDate(nextStartDate);
                setEndDate(nextEndDate);
              }}
              focusedInput={focusedInput}
              onFocusChange={setFocusedInput}
              minimumNights={0}
              isOutsideRange={() => false}
              displayFormat="DD/MM/YYYY"
              numberOfMonths={1}
              small
              hideKeyboardShortcutsPanel
              showClearDates
              startDatePlaceholderText="From date"
              endDatePlaceholderText="To date"
            />
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white/85 px-6 py-12 text-center shadow-sm">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-teal-100 text-teal-700">
              <FaChartBar />
            </div>
            <h2 className="mt-3 text-lg font-black text-slate-900">
              No automation report data found
            </h2>
            <p className="mt-2 text-xs text-slate-500">
              Try another date range, or open an event after its provider report becomes available.
            </p>
          </div>
        ) : (
          items.map((event) => (
            <div
              key={event.event_id}
              className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-black text-slate-900">
                      {event.event_type || "Event"}
                    </h2>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
                      Event ID #{event.event_id}
                    </span>
                  </div>
                  <div className="mt-2 grid gap-1 text-xs text-slate-600 lg:grid-cols-2">
                    <span className="font-semibold">{formatDateTime(event.event_date)}</span>
                    <span>{event.customer_name || "-"}</span>
                    <span className="font-bold text-teal-700">{event.customer_mobile || "-"}</span>
                    <span className="truncate">{[event.event_city, event.event_address].filter(Boolean).join(", ") || "-"}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    dispatch(setShowPreloader(true));
                    const detail = await getEventAutomationReportDetail(event.event_id);
                    dispatch(setShowPreloader(false));
                    if (detail) setDetailData(detail);
                  }}
                  className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-xs font-black text-slate-700 transition hover:bg-white"
                >
                  View detailed report
                </button>
              </div>

              <div className="mt-3 grid gap-3 xl:grid-cols-2">
                {isChannelEnabled(event.sms_report) ? (
                  <ChannelCard icon={FaSms} title="Reminder SMS" report={event.sms_report} tone="sms" />
                ) : null}
                {isChannelEnabled(event.voice_report) ? (
                  <ChannelCard icon={FaPhoneAlt} title="Reminder Voice Call" report={event.voice_report} tone="voice" />
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>

      {pagination.total > 0 && (
        <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold text-slate-900">
              Page {pagination.page} of {Math.max(pagination.total_pages || 1, 1)}
            </p>
            <p className="text-[11px] text-slate-500">
              Showing {Math.min((pagination.page - 1) * pagination.per_page + 1, pagination.total)}-
              {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} events
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FaChevronLeft />
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() =>
                setPage((current) => Math.min(current + 1, pagination.total_pages || current + 1))
              }
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      <CustomModal
        show={Boolean(detailData)}
        onClose={() => setDetailData(null)}
        title="Automation Detail"
        size="lg"
      >
        {detailData ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-black text-slate-900">
                  {detailData.event?.event_type || "Event"}
                </h2>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
                  Event ID #{detailData.event?.event_id}
                </span>
              </div>
              <div className="mt-2 grid gap-1 text-xs text-slate-600 lg:grid-cols-2">
                <span>{formatDateTime(detailData.event?.event_date)}</span>
                <span>{detailData.event?.customer_name || "-"}</span>
                <span>{detailData.event?.customer_mobile || "-"}</span>
                <span>{[detailData.event?.event_city, detailData.event?.event_address].filter(Boolean).join(", ") || "-"}</span>
              </div>
            </div>

            <div className="grid gap-5 xl:grid-cols-2">
              {isChannelEnabled(detailData.sms_report) ? (
                <DetailList title="SMS Recipient Detail" items={detailData.sms_items || []} />
              ) : null}
              {isChannelEnabled(detailData.voice_report) ? (
                <DetailList title="Voice Recipient Detail" items={detailData.voice_items || []} />
              ) : null}
            </div>
          </div>
        ) : null}
      </CustomModal>
    </div>
  );
};

export default OperatorAutomationReport;
